import { useState } from 'react';
import { Link } from 'react-router-dom';
import { COLORS } from '../../constants/theme';
import { Button } from '../ui/Button';
import { NumericInput } from '../strategy/NumericInput';
import { GlanceCard } from '../shell/GlanceCard';
import { ConfirmModal } from '../ConfirmModal';
import { useToast } from '../../hooks/useToast';
import { formatNumber } from '../../utils/format';
import { computeUnitSurplus } from '../../utils/unitStockMath';
import { api } from '../../api/client';
import type { Province } from '../../types/province';
import type { RaceConfig } from '../../api/gameConfig';
import type { ActiveStrategyDto } from '../../types/strategy';
import type { ReleaseRequest, ReleaseResult, TrainableUnitType } from '../../types/train';

const RELEASABLE_UNITS = [
  { type: 'OFFENSE_SPECIALIST', key: 'offenseSpecialist' as const, releasesInto: 'Soldiers' },
  { type: 'DEFENSE_SPECIALIST', key: 'defenseSpecialist' as const, releasesInto: 'Soldiers' },
  { type: 'ELITE', key: 'elite' as const, releasesInto: 'Soldiers' },
  { type: 'THIEF', key: null, releasesInto: 'Soldiers' },
  { type: 'SOLDIER', key: null, releasesInto: 'Peasants' },
] as const;

/** SF-179 Component 2: Soldiers are raw material — peasant-drafted, feeding Training/Exploration
 *  rather than being a standing unit type strategy conforms toward — and the draft engine already
 *  self-limits soldier count via Draft %. Bulk-releasing them via a one-tap conformance action would
 *  discard economy-critical stock for reasons unrelated to strategy conformance. */
const STRATEGY_ELIGIBLE_TYPES: readonly TrainableUnitType[] = ['OFFENSE_SPECIALIST', 'DEFENSE_SPECIALIST', 'ELITE', 'THIEF'];

interface ReleaseTableProps {
  province: Province;
  raceConfig: RaceConfig | null;
  worldId: string;
  provinceId: string;
  releaseQty: Record<string, number>;
  onReleaseQtyChange: (qty: Record<string, number>) => void;
  onSuccess: () => void;
  /** SF-141: 2-line stacked-row layout wrapped in a `GlanceCard`, no column-header row, for the
   *  mobile Release tab. `false` (default) is the existing 4-column grid, unchanged. */
  compact?: boolean;
  /** SF-179 Component 2: sourced from `Training.tsx`, the single source of truth for strategy
   *  targets — avoids re-deriving the armyComp/thiefPA key-mapping here. */
  activeStrategy: ActiveStrategyDto | null;
  getTargetOrNull: (unitType: TrainableUnitType) => number | null;
  /** SF-179 Component 2 (Decision 6): threaded from `Training.tsx`'s existing `bulkMutationVersion`
   *  — clears a mid-edit row's stale typed draft when a bulk prefill applies. */
  resetKey?: number;
  /** SF-179 Component 2 (Decision 6): called after any external bulk write to `releaseQty` (Release
   *  to Strategy bulk/per-row, Clear, submit-success) so the parent can bump `bulkMutationVersion`. */
  onBulkMutation: () => void;
}

function getUnitName(
  unitType: string,
  unitKey: 'offenseSpecialist' | 'defenseSpecialist' | 'elite' | null,
  raceConfig: RaceConfig | null,
): string {
  if (unitKey && raceConfig) return raceConfig.units[unitKey].name;
  if (unitType === 'THIEF') return 'Thieves';
  if (unitType === 'SOLDIER') return 'Soldiers';
  return unitType;
}

function getUnitDisplayName(
  unitType: string,
  unitKey: 'offenseSpecialist' | 'defenseSpecialist' | 'elite' | null,
  raceConfig: RaceConfig | null,
): string {
  const name = getUnitName(unitType, unitKey, raceConfig);
  if (unitKey && raceConfig) {
    const stats = raceConfig.units[unitKey];
    return `${name} (${stats.offense}/${stats.defense})`;
  }
  if (unitType === 'THIEF') return `${name} (0/0)`;
  if (unitType === 'SOLDIER') return `${name} (1/1)`;
  return name;
}

function getOwnedCount(unitType: string, province: Province): number {
  switch (unitType) {
    case 'OFFENSE_SPECIALIST': return province.population.offenseSpecialists;
    case 'DEFENSE_SPECIALIST': return province.population.defenseSpecialists;
    case 'ELITE': return province.population.elites;
    case 'THIEF': return province.population.thieves;
    case 'SOLDIER': return province.population.soldiers;
    default: return 0;
  }
}

function isStrategyEligible(unitType: string): unitType is TrainableUnitType {
  return (STRATEGY_ELIGIBLE_TYPES as readonly string[]).includes(unitType);
}

const gridTemplate = '1fr 64px 70px 90px';

export function ReleaseTable({
  province,
  raceConfig,
  worldId,
  provinceId,
  releaseQty,
  onReleaseQtyChange,
  onSuccess,
  compact = false,
  activeStrategy,
  getTargetOrNull,
  resetKey,
  onBulkMutation,
}: ReleaseTableProps) {
  const { addToast } = useToast();
  const [isReleasingUnits, setIsReleasingUnits] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  function surplusFor(unitType: string, owned: number): number {
    if (!isStrategyEligible(unitType)) return 0;
    const target = getTargetOrNull(unitType);
    return computeUnitSurplus({ target, owned }).surplus;
  }

  const handleClear = () => {
    onReleaseQtyChange({});
    onBulkMutation();
  };

  const handleReleaseToStrategy = () => {
    if (!activeStrategy?.settings) return;
    const next = { ...releaseQty };
    for (const unit of RELEASABLE_UNITS) {
      if (!isStrategyEligible(unit.type)) continue;
      const owned = getOwnedCount(unit.type, province);
      next[unit.type] = surplusFor(unit.type, owned);
    }
    onReleaseQtyChange(next);
    onBulkMutation();
  };

  const handlePerRowRelease = (unitType: string) => {
    const owned = getOwnedCount(unitType, province);
    const surplus = surplusFor(unitType, owned);
    if (surplus <= 0) return;
    onReleaseQtyChange({ ...releaseQty, [unitType]: surplus });
    onBulkMutation();
  };

  const handleSubmit = async () => {
    setShowConfirmModal(false);
    setIsReleasingUnits(true);

    const releasesToProcess = Object.entries(releaseQty)
      .filter(([, qty]) => qty > 0)
      .map(([unitType, quantity]) => ({ unitType, quantity }));

    if (releasesToProcess.length === 0) {
      addToast('Please enter at least one unit to release', 'failure');
      setIsReleasingUnits(false);
      return;
    }

    // SF-179 Component 2 (Decision 4): continue-on-error, not abort-on-first-error —
    // `ReleaseTable.getOwnedCount()` sources from `population.*`, which includes units away on
    // attack, while the server validates at-home-only. A bulk pre-fill can exceed what the server
    // accepts whenever the player's army is out; aborting on the first failure left an ambiguous
    // partial release. Attempt every unit type, collect per-type success/failure, surface one
    // message naming exactly what happened.
    const successes: string[] = [];
    const failures: string[] = [];
    // Preserve failed unit types' quantities for retry — only succeeded types get cleared, so a
    // partial failure doesn't force the player to re-derive/re-enter what didn't go through.
    const failedQty: Record<string, number> = {};

    for (const { unitType, quantity } of releasesToProcess) {
      const unitDef = RELEASABLE_UNITS.find((u) => u.type === unitType);
      const unitName = getUnitName(unitType, unitDef?.key ?? null, raceConfig);
      try {
        const request: ReleaseRequest = { provinceId, unitType, quantity };
        const response = await api.post<ReleaseResult>(
          `/api/worlds/${worldId}/provinces/${provinceId}/release`,
          request
        );

        if (response.error) {
          failures.push(`${unitName} failed: ${response.error}`);
          failedQty[unitType] = quantity;
        } else if (response.data) {
          successes.push(`${formatNumber(quantity)} ${unitName}`);
        }
      } catch {
        failures.push(`${unitName} failed: request error`);
        failedQty[unitType] = quantity;
      }
    }

    const parts: string[] = [];
    if (successes.length > 0) parts.push(`Released ${successes.join(', ')}.`);
    if (failures.length > 0) parts.push(failures.join(' '));
    addToast(parts.join(' '), failures.length > 0 ? 'failure' : 'success');

    if (successes.length > 0) {
      onReleaseQtyChange(failedQty);
      onBulkMutation();
      onSuccess();
    }

    setIsReleasingUnits(false);
  };

  const hasAnyQty = Object.values(releaseQty).some((q) => q > 0);
  const totalReleasing = Object.values(releaseQty).reduce((s, q) => s + q, 0);

  // SF-179 Component 2 (Critical Issue #6): pre-fill-only, never auto-submits. Submitting a total
  // above 10 units gates behind a plain (non-typed) ConfirmModal, mirroring
  // `RazePanel.tsx:84-88`'s exact threshold.
  const handleSubmitClick = () => {
    if (!hasAnyQty) return;
    if (totalReleasing > 10) {
      setShowConfirmModal(true);
    } else {
      handleSubmit();
    }
  };

  const releaseUnitList = Object.entries(releaseQty)
    .filter(([, qty]) => qty > 0)
    .map(([unitType, qty]) => {
      const unitDef = RELEASABLE_UNITS.find((u) => u.type === unitType);
      const name = getUnitName(unitType, unitDef?.key ?? null, raceConfig);
      return `${formatNumber(qty)} ${name}`;
    });

  const releaseToStrategyDisabled = !activeStrategy?.settings;

  const quickActionsRow = (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={handleReleaseToStrategy}
        aria-disabled={releaseToStrategyDisabled}
        className="flex items-center gap-1.5 text-xs font-semibold px-3 rounded-lg"
        style={{
          minHeight: 44,
          backgroundColor: !releaseToStrategyDisabled ? 'rgba(212,167,44,0.08)' : 'transparent',
          border: `1px solid ${!releaseToStrategyDisabled ? 'rgba(212,167,44,0.35)' : COLORS.border}`,
          color: !releaseToStrategyDisabled ? COLORS.gold : COLORS.text.tertiary,
          cursor: !releaseToStrategyDisabled ? 'pointer' : 'default',
        }}
      >
        ⚡ Release to Strategy
      </button>
    </div>
  );

  const noStrategyNote = releaseToStrategyDisabled ? (
    <div className="text-xs" style={{ color: COLORS.text.tertiary }}>
      No active strategy —{' '}
      <Link to="/strategy" style={{ color: COLORS.link }}>
        set one to see target guidance
      </Link>
      .
    </div>
  ) : null;

  const submitBar = (
    <div
      className="px-4 py-3 space-y-3"
      style={{ borderTop: `1px solid ${COLORS.border}` }}
    >
      <div className="flex items-center gap-6 text-sm">
        <span className="whitespace-nowrap">
          <span style={{ color: COLORS.text.tertiary }}>Releasing: </span>
          <span className="font-mono" style={{ color: COLORS.text.primary }}>
            {formatNumber(totalReleasing)} units
          </span>
        </span>
      </div>

      <div className="flex items-center">
        <div className="flex gap-2 ml-auto">
          <Button variant="secondary" onClick={handleClear} disabled={!hasAnyQty}>
            Clear
          </Button>
          <Button onClick={handleSubmitClick} isLoading={isReleasingUnits} disabled={!hasAnyQty}>
            Release Units
          </Button>
        </div>
      </div>
    </div>
  );

  const confirmModal = (
    <ConfirmModal
      isOpen={showConfirmModal}
      onConfirm={handleSubmit}
      onCancel={() => setShowConfirmModal(false)}
      title="Confirm Release"
      confirmText="Confirm Release"
    >
      <div>
        <p className="mb-3">
          Are you sure you want to release {formatNumber(totalReleasing)} units?
        </p>
        <ul className="list-disc list-inside mb-3 space-y-1 text-sm">
          {releaseUnitList.map((item, idx) => (
            <li key={idx}>{item}</li>
          ))}
        </ul>
      </div>
    </ConfirmModal>
  );

  if (compact) {
    return (
      <>
        <GlanceCard title="Release Units">
          {quickActionsRow}
          {noStrategyNote}
          {RELEASABLE_UNITS.map((unit) => {
            const owned = getOwnedCount(unit.type, province);
            const qty = releaseQty[unit.type] || 0;
            const eligible = isStrategyEligible(unit.type);
            const target = eligible ? getTargetOrNull(unit.type) : null;
            const surplus = surplusFor(unit.type, owned);

            let row2: string;
            if (!eligible) {
              row2 = `→ ${unit.releasesInto}`;
            } else if (target === null) {
              row2 = 'No target set';
            } else if (surplus <= 0) {
              row2 = `target ${formatNumber(target)} · at/below target`;
            } else {
              row2 = `target ${formatNumber(target)} · surplus ${formatNumber(surplus)}`;
            }

            const unitName = getUnitName(unit.type, unit.key, raceConfig);

            return (
              <div
                key={unit.type}
                className="flex flex-col gap-1 py-1.5"
                style={{ borderBottom: `1px solid ${COLORS.border}` }}
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium truncate min-w-0 flex-1" style={{ color: COLORS.text.primary }}>
                    {getUnitDisplayName(unit.type, unit.key, raceConfig)}
                  </span>
                  <span className="text-sm font-mono flex-shrink-0" style={{ color: COLORS.text.primary }}>
                    {formatNumber(owned)}
                  </span>
                  <NumericInput
                    value={qty}
                    max={owned}
                    onChange={(v) => onReleaseQtyChange({ ...releaseQty, [unit.type]: v })}
                    className="w-16"
                    size="touch"
                    resetKey={resetKey}
                    ariaLabel={`Release ${unitName} quantity`}
                  />
                  {eligible && (
                    <button
                      type="button"
                      onClick={() => handlePerRowRelease(unit.type)}
                      aria-disabled={surplus <= 0}
                      aria-label={`Release ${unitName} to strategy`}
                      style={{
                        width: 44,
                        height: 44,
                        fontSize: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '8px',
                        border: `1px solid ${COLORS.border}`,
                        backgroundColor: COLORS.sectionHeader,
                        color: '#B89878',
                        cursor: surplus <= 0 ? 'default' : 'pointer',
                        opacity: surplus <= 0 ? 0.4 : 1,
                        flexShrink: 0,
                      }}
                    >
                      ⚡
                    </button>
                  )}
                </div>
                <div className="text-[11px]" style={{ color: COLORS.text.tertiary }}>
                  {row2}
                </div>
              </div>
            );
          })}
          {submitBar}
        </GlanceCard>
        {confirmModal}
      </>
    );
  }

  return (
    <>
      <div
        className="rounded overflow-hidden"
        style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}` }}
      >
        <div className="px-3 py-2 flex flex-col gap-1.5" style={{ borderBottom: `1px solid ${COLORS.border}` }}>
          {quickActionsRow}
          {noStrategyNote}
        </div>

        {/* Header */}
        <div
          className="grid items-center px-3 py-1.5"
          style={{
            gridTemplateColumns: gridTemplate,
            backgroundColor: COLORS.sectionHeader,
          }}
        >
          <span className="text-xs font-bold uppercase tracking-wider" style={{ color: COLORS.text.primary }}>Unit (Off/Def)</span>
          <span className="text-right text-xs uppercase tracking-wide" style={{ color: COLORS.text.tertiary }}>HAVE</span>
          <span className="text-right text-xs uppercase tracking-wide" style={{ color: COLORS.text.tertiary }}>→ INTO</span>
          <span className="text-right text-xs uppercase tracking-wide" style={{ color: COLORS.text.tertiary }}>RELEASE</span>
        </div>

        {RELEASABLE_UNITS.map((unit, rowIndex) => {
          const owned = getOwnedCount(unit.type, province);
          const qty = releaseQty[unit.type] || 0;
          const eligible = isStrategyEligible(unit.type);
          const surplus = surplusFor(unit.type, owned);
          const unitName = getUnitName(unit.type, unit.key, raceConfig);

          return (
            <div
              key={unit.type}
              className="grid items-center px-3 py-1.5"
              style={{
                gridTemplateColumns: gridTemplate,
                borderBottom: `1px solid ${COLORS.border}`,
                backgroundColor: rowIndex % 2 === 0 ? COLORS.tableRowAlt : COLORS.card,
              }}
            >
              <span className="text-sm" style={{ color: COLORS.text.primary }}>
                {getUnitDisplayName(unit.type, unit.key, raceConfig)}
              </span>
              <span className="text-right font-mono text-sm" style={{ color: COLORS.text.secondary }}>
                {formatNumber(owned)}
              </span>
              <span className="text-right text-sm" style={{ color: COLORS.text.tertiary }}>
                {unit.releasesInto}
              </span>
              <div className="flex items-center justify-end gap-1">
                {eligible && (
                  <button
                    type="button"
                    onClick={() => handlePerRowRelease(unit.type)}
                    aria-disabled={surplus <= 0}
                    aria-label={`Release ${unitName} to strategy`}
                    style={{
                      width: '26px',
                      height: '26px',
                      fontSize: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '4px',
                      border: `1px solid ${COLORS.border}`,
                      backgroundColor: COLORS.sectionHeader,
                      color: '#B89878',
                      cursor: surplus <= 0 ? 'default' : 'pointer',
                      opacity: surplus <= 0 ? 0.4 : 1,
                      flexShrink: 0,
                    }}
                  >
                    ⚡
                  </button>
                )}
                <NumericInput
                  value={qty}
                  max={owned}
                  onChange={(v) => onReleaseQtyChange({ ...releaseQty, [unit.type]: v })}
                  className="w-16"
                  resetKey={resetKey}
                />
              </div>
            </div>
          );
        })}

        {submitBar}
      </div>
      {confirmModal}
    </>
  );
}
